import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Users, Trash2, RefreshCw } from 'lucide-react';
import YabbitUsersModal from '../components/YabbitUsersModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = () => {
  const [branding, setBranding] = useState({ company_name: '', logo_url: '', primary_color: '#09D6B4' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [yabbitStatus, setYabbitStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [deletingCalls, setDeletingCalls] = useState(false);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await axios.get(`${API}/branding`);
        setBranding(response.data);
      } catch {
        console.error('Failed to load branding settings');
      } finally {
        setLoading(false);
      }
    };
    const testYabbitConnection = async () => {
      try {
        const response = await axios.get(`${API}/yabbit/test-connection`);
        setYabbitStatus(response.data);
      } catch {
        setYabbitStatus({ connected: false, message: 'Failed to connect' });
      }
    };
    fetchBranding();
    testYabbitConnection();
    axios.get(`${API}/yabbit/selected-users`)
      .then(r => setSelectedUsers(r.data.selected_users || []))
      .catch(() => {});
  }, []);

  const handleDeleteYabbitCalls = async () => {
    if (!window.confirm('Delete all calls synced from Yabbit? This cannot be undone.')) return;
    setDeletingCalls(true);
    try {
      const r = await axios.delete(`${API}/yabbit/calls`);
      toast.success(`Deleted ${r.data.deleted_calls} calls and ${r.data.deleted_gradings} gradings`);
    } catch {
      toast.error('Failed to delete calls');
    } finally {
      setDeletingCalls(false);
    }
  };

  const testYabbitConnection = async () => {
    try {
      const response = await axios.get(`${API}/yabbit/test-connection`);
      setYabbitStatus(response.data);
    } catch {
      setYabbitStatus({ connected: false, message: 'Failed to connect' });
    }
  };

  const handleSyncYabbit = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/yabbit/sync`, { days_back: 7, limit: 100 }, { timeout: 25000 });
      const d = response.data;
      toast.success(`Sync started! ${d.synced ?? 0} new calls imported.`);
      if (d.skipped) toast.info(`${d.skipped} already-imported calls skipped.`);
      if (d.grading === 'started_in_background') toast.info('AI grading running in background — check Calls list in a few minutes.');
    } catch (error) {
      // "context deadline exceeded" = request timed out at proxy but sync is still running
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.success('Sync is running in background (may take a few minutes). Check Calls list shortly.');
      } else {
        toast.error(error.response?.data?.detail || 'Sync failed');
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.put(`${API}/branding`, branding);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
          Settings
        </h1>
        <p className="text-slate-600">Configure your call coaching portal</p>
      </div>

      <Card className="border-slate-200 shadow-sm max-w-2xl">
        <CardHeader>
          <CardTitle>Whitelabel Branding</CardTitle>
          <CardDescription>Customize the appearance of your portal</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name</Label>
              <Input
                id="company_name"
                value={branding.company_name}
                onChange={(e) => setBranding({...branding, company_name: e.target.value})}
                placeholder="Your Company Name"
                data-testid="company-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="logo_url">Logo URL (Optional)</Label>
              <Input
                id="logo_url"
                type="url"
                value={branding.logo_url || ''}
                onChange={(e) => setBranding({...branding, logo_url: e.target.value})}
                placeholder="https://example.com/logo.png"
                data-testid="logo-url-input"
              />
              <p className="text-xs text-slate-500">Enter a URL to your company logo</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="primary_color">Primary Color</Label>
              <div className="flex items-center space-x-3">
                <Input
                  id="primary_color"
                  type="color"
                  value={branding.primary_color}
                  onChange={(e) => setBranding({...branding, primary_color: e.target.value})}
                  className="w-20 h-10"
                  data-testid="primary-color-input"
                />
                <Input
                  type="text"
                  value={branding.primary_color}
                  onChange={(e) => setBranding({...branding, primary_color: e.target.value})}
                  placeholder="#4F46E5"
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-slate-500">Choose your brand's primary color</p>
            </div>

            <div className="pt-4 border-t border-slate-200">
              <Button
                type="submit"
                disabled={saving}
                className="w-full sm:w-auto"
                data-testid="save-settings-btn"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm max-w-2xl">
        <CardHeader>
          <CardTitle>Yabbit UCaaS Integration</CardTitle>
          <CardDescription>Sync call recordings from your Yabbit system</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
              <h4 className="font-semibold text-slate-900 mb-3">Connection Status</h4>
              {yabbitStatus ? (
                <div className="flex items-center space-x-3">
                  {yabbitStatus.connected ? (
                    <>
                      <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></div>
                      <span className="text-emerald-700 font-medium">
                        Connected — v{yabbitStatus.version} ({yabbitStatus.hostname})
                      </span>
                    </>
                  ) : (
                    <>
                      <div className="w-3 h-3 bg-rose-500 rounded-full"></div>
                      <span className="text-rose-700 font-medium">Not Connected — {yabbitStatus.error}</span>
                    </>
                  )}
                </div>
              ) : (
                <p className="text-slate-600">Testing connection...</p>
              )}
              {yabbitStatus?.domain && (
                <p className="text-sm text-slate-500 mt-2">Domain: {yabbitStatus.domain}</p>
              )}
            </div>

            <div className="space-y-3">
              {/* User selection */}
              <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="font-semibold text-slate-900 text-sm">Sync Users</h4>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {selectedUsers.length > 0
                        ? `${selectedUsers.length} extension${selectedUsers.length !== 1 ? 's' : ''} selected: ${selectedUsers.slice(0,5).join(', ')}${selectedUsers.length > 5 ? '...' : ''}`
                        : 'No filter — all extensions will be included'}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowUsersModal(true)}
                    disabled={!yabbitStatus?.connected}
                    data-testid="configure-users-btn"
                  >
                    <Users className="w-4 h-4 mr-1" />
                    {selectedUsers.length > 0 ? 'Edit Selection' : 'Select Users'}
                  </Button>
                </div>
              </div>

              {/* Sync button */}
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={handleSyncYabbit}
                  disabled={syncing || !yabbitStatus?.connected}
                  data-testid="sync-yabbit-btn"
                >
                  {syncing ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Syncing...</>
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-2" />Sync Calls from Yabbit</>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDeleteYabbitCalls}
                  disabled={deletingCalls || !yabbitStatus?.connected}
                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200"
                  data-testid="delete-yabbit-calls-btn"
                >
                  {deletingCalls ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Deleting...</>
                  ) : (
                    <><Trash2 className="w-4 h-4 mr-2" />Delete Synced Calls</>
                  )}
                </Button>
              </div>
            </div>

            <p className="text-sm text-slate-500">
              Imports calls from the last 7 days and automatically grades them with AI
            </p>

            {!yabbitStatus?.connected && (
              <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Yabbit integration requires valid API credentials.
                  Contact Yabbit support for API access or verify your credentials in the backend configuration.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm max-w-2xl">
        <CardHeader>
          <CardTitle>Integration Information</CardTitle>
          <CardDescription>Details about your Yabbit UCaaS integration</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
              <h4 className="font-semibold text-slate-900 mb-2">API Endpoint</h4>
              <code className="text-sm text-slate-600">{BACKEND_URL}/api/calls</code>
              <p className="text-xs text-slate-500 mt-2">
                Send POST requests to this endpoint to create new call records
              </p>
            </div>

            <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
              <h4 className="font-semibold text-slate-900 mb-2">Required Fields</h4>
              <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                <li>call_id: Unique identifier from Yabbit</li>
                <li>agent_id: Agent identifier</li>
                <li>agent_name: Agent's full name</li>
                <li>call_type: used_car_sales, car_service, or real_estate</li>
                <li>transcription: Full call transcript text</li>
                <li>duration_seconds: Call duration in seconds</li>
                <li>call_date: ISO formatted date-time</li>
              </ul>
            </div>

            <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">AI Grading</h4>
              <p className="text-sm text-blue-800">
                Calls are automatically analyzed using OpenAI GPT-5.2. Grading happens asynchronously 
                and email alerts are sent when scores fall below the threshold defined in each knowledge base.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    <YabbitUsersModal
      open={showUsersModal}
      onClose={() => setShowUsersModal(false)}
      onSaved={(list) => setSelectedUsers(list)}
    />
    </>
  );
};

export default SettingsPage;
